import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux-stores/store';
import useOutsideClick from '../../hooks/useOutsideClick';
import { Link } from 'react-router-dom';
import { INIT_IG_AUTH_ROUTE, MOBILE_APP_URLS } from '../../constants/constants';
import { addAppDownloadUTMParams, getMobileOSIfWeb } from '../../utils/helpers';

interface MainMenuProps {
    visible: boolean,
    hide: () => void;
}

interface MenuItem {
    label: string,
    link: string,
    icon_classes: string
    id: string
    native?: boolean
}

const MenuLink: React.FC<MenuItem & { hideMenu: () => void }> = ({ label, link, icon_classes, id, hideMenu, native }) => {
    if (native) {
        return (
            <a id={id} href={link} target='_blank' rel="noreferrer" onClick={hideMenu}>
                <i className={icon_classes + " text-gray-800"}></i>
                <span className='text-gray-800'>{label}</span>
                <i className="fa fa-angle-right"></i>
            </a>
        )
    }
    return (
        <Link id={id} to={link} onClick={hideMenu}>
            <i className={icon_classes + " text-gray-800"}></i>
            <span className='text-gray-800'>{label}</span>
            <i className="fa fa-angle-right"></i>
        </Link>
    );
}

const menuItems: MenuItem[] = [
    {
        label: "Campaigns",
        link: '/',
        icon_classes: "fa fa-dollar",
        id: "nav-campaigns"
    },
    {
        label: "My Collabs",
        link: '/collaborations',
        icon_classes: "fa fa-star",
        id: "nav-collaborations"
    },
    {
        label: "Referrals",
        link: '/referral',
        icon_classes: "fa fa-heart",
        id: "nav-referrals"
    },
    {
        label: "Payments",
        link: '/wallet',
        icon_classes: "fa fa-wallet",
        id: "nav-payments"
    },
    {
        label: "Membership",
        link: '/membership',
        icon_classes: "fa fa-credit-card",
        id: "nav-membership"
    },
    {
        label: "Profile",
        link: '/i/me',
        icon_classes: "fa fa-user",
        id: "nav-profile"
    },
    {
        label: "Smart Links",
        link: '/smart-links',
        icon_classes: "fa-solid fa-link",
        id: "nav-smartlinks"
    },
    {
        label: "Logout",
        link: '/logout',
        icon_classes: "fa fa-right-from-bracket",
        id: "nav-logout"
    }
]

const testUserMenuItems: MenuItem[] = [
    {
        label: 'Connect Instagram',
        link: INIT_IG_AUTH_ROUTE,
        icon_classes: 'fab fa-instagram',
        id: 'connect-ig'
    }
]

const mobileOS = getMobileOSIfWeb();

const newMenuItems = [
    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 14C2 10.2288 2 8.34315 3.17157 7.17157C4.34315 6 6.22876 6 10 6H14C17.7712 6 19.6569 6 20.8284 7.17157C22 8.34315 22 10.2288 22 14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14Z" stroke="#4D4D4D" strokeWidth="1.5" />
                    <path d="M16 6C16 4.11438 16 3.17157 15.4142 2.58579C14.8284 2 13.8856 2 12 2C10.1144 2 9.17157 2 8.58579 2.58579C8 3.17157 8 4.11438 8 6" stroke="#4D4D4D" strokeWidth="1.5" />
                    <path d="M12 17.3333C13.1046 17.3333 14 16.5871 14 15.6667C14 14.7462 13.1046 14 12 14C10.8954 14 10 13.2538 10 12.3333C10 11.4129 10.8954 10.6667 12 10.6667M12 17.3333C10.8954 17.3333 10 16.5871 10 15.6667M12 17.3333V18M12 10V10.6667M12 10.6667C13.1046 10.6667 14 11.4129 14 12.3333" stroke="#4D4D4D" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </span>
        ),
        label: 'Campaigns',
        url: '/'
    },
    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.85929 1.25001C6.88904 1.25001 6.91919 1.25002 6.94976 1.25002L6.98675 1.25001C7.33818 1.24999 7.56433 1.24998 7.78542 1.27065C8.68728 1.35499 9.54516 1.69531 10.2586 2.25002H16.5C16.5196 2.25002 16.5389 2.25001 16.5579 2.25001C16.9666 2.24994 17.2449 2.2499 17.4895 2.2821C19.1722 2.50364 20.4964 3.82779 20.7179 5.51054C20.7263 5.57397 20.7325 5.63966 20.737 5.70931C21.0145 5.83579 21.2715 5.99934 21.5077 6.21185C21.6061 6.30032 21.6997 6.39394 21.7882 6.49231C22.3165 7.07965 22.5422 7.79459 22.648 8.63601C22.75 9.4479 22.75 10.4741 22.75 11.747V14.0564C22.75 15.8942 22.75 17.3498 22.5969 18.489C22.4393 19.6615 22.1071 20.6104 21.3588 21.3588C20.6104 22.1071 19.6615 22.4393 18.489 22.5969C17.3498 22.75 15.8942 22.75 14.0564 22.75H9.94361C8.10584 22.75 6.65021 22.75 5.51099 22.5969C4.33857 22.4393 3.38962 22.1071 2.64126 21.3588C1.8929 20.6104 1.56078 19.6615 1.40315 18.489C1.24999 17.3498 1.25 15.8942 1.25002 14.0564L1.25002 6.94976C1.25002 6.91919 1.25001 6.88904 1.25001 6.85929C1.2499 6.06338 1.24982 5.55685 1.33237 5.11935C1.6949 3.19788 3.19788 1.6949 5.11935 1.33237C5.55685 1.24982 6.06338 1.2499 6.85929 1.25001ZM19.1474 5.32768C18.8895 4.5029 18.1732 3.88506 17.2937 3.76927C17.1598 3.75163 16.9883 3.75002 16.5 3.75002H11.8113C12.4542 4.38908 12.7459 4.65598 13.0768 4.84005C13.2948 4.96134 13.526 5.05713 13.766 5.12552C14.1793 5.24333 14.6324 5.25002 15.8284 5.25002L16.253 5.25002C17.4153 5.25 18.3718 5.24999 19.1474 5.32768ZM6.94976 2.75002C6.03312 2.75002 5.67873 2.75329 5.39746 2.80636C4.08277 3.05441 3.05441 4.08277 2.80636 5.39746C2.75329 5.67873 2.75002 6.03312 2.75002 6.94976V14C2.75002 15.9068 2.75161 17.2615 2.88978 18.2892C3.02504 19.2953 3.27871 19.8749 3.70192 20.2981C4.12513 20.7213 4.70478 20.975 5.71087 21.1103C6.73853 21.2484 8.0932 21.25 10 21.25H14C15.9068 21.25 17.2615 21.2484 18.2892 21.1103C19.2953 20.975 19.8749 20.7213 20.2981 20.2981C20.7213 19.8749 20.975 19.2953 21.1103 18.2892C21.2484 17.2615 21.25 15.9068 21.25 14V11.7979C21.25 10.4621 21.2486 9.5305 21.1597 8.82312C21.0731 8.13448 20.9141 7.76356 20.6729 7.49539C20.6198 7.43637 20.5637 7.3802 20.5046 7.32712C20.2365 7.08592 19.8656 6.92692 19.1769 6.84034C18.4695 6.75141 17.538 6.75002 16.2021 6.75002H15.8284C15.7912 6.75002 15.7545 6.75002 15.7182 6.75003C14.6702 6.75025 13.9944 6.75038 13.3548 6.56806C13.0041 6.46811 12.6661 6.32811 12.3475 6.15083C11.7663 5.82747 11.2885 5.3495 10.5476 4.60833C10.522 4.58265 10.496 4.55666 10.4697 4.53035L9.91943 3.98009C9.63616 3.69682 9.52778 3.58951 9.41731 3.49793C8.91403 3.08073 8.29664 2.825 7.64576 2.76413C7.50289 2.75077 7.35038 2.75002 6.94976 2.75002ZM12.25 10C12.25 9.5858 12.5858 9.25002 13 9.25002H18C18.4142 9.25002 18.75 9.5858 18.75 10C18.75 10.4142 18.4142 10.75 18 10.75H13C12.5858 10.75 12.25 10.4142 12.25 10Z" fill="#4D4D4D" />
                </svg>
            </span>
        ),
        label: 'My Collaborations',
        url: '/collaborations'
    },
    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.1625 18.4876L13.4417 19.2084C11.053 21.5971 7.18019 21.5971 4.79151 19.2084C2.40283 16.8198 2.40283 12.9469 4.79151 10.5583L5.51236 9.8374" stroke="#4D4D4D" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M9.83789 14.1625L14.163 9.8374" stroke="#4D4D4D" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M9.83789 5.51236L10.5587 4.79151C12.9474 2.40283 16.8202 2.40283 19.2089 4.79151C21.5976 7.18019 21.5976 11.053 19.2089 13.4417L18.4881 14.1625" stroke="#4D4D4D" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </span>
        ),
        label: 'SmartLinks',
        url: '/smart-links'
    },

    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M18.2892 4.88976C17.2615 4.75159 15.9068 4.75 14 4.75L10 4.75C8.09318 4.75 6.73851 4.75159 5.71085 4.88976C4.70476 5.02503 4.12511 5.27869 3.7019 5.7019C3.27869 6.12511 3.02502 6.70476 2.88976 7.71085C2.75159 8.73851 2.75 10.0932 2.75 12C2.75 13.9068 2.75159 15.2615 2.88976 16.2892C3.02502 17.2952 3.27869 17.8749 3.7019 18.2981C4.12511 18.7213 4.70476 18.975 5.71085 19.1102C6.73851 19.2484 8.09318 19.25 10 19.25H14C15.9068 19.25 17.2615 19.2484 18.2892 19.1102C19.2952 18.975 19.8749 18.7213 20.2981 18.2981C20.7213 17.8749 20.975 17.2952 21.1102 16.2892C21.2484 15.2615 21.25 13.9068 21.25 12C21.25 10.0932 21.2484 8.73851 21.1102 7.71085C20.975 6.70476 20.7213 6.12511 20.2981 5.7019C19.8749 5.27869 19.2952 5.02502 18.2892 4.88976ZM18.489 3.40314C19.6614 3.56076 20.6104 3.89288 21.3588 4.64124C22.1071 5.38961 22.4392 6.33856 22.5969 7.51098C22.75 8.65019 22.75 10.1058 22.75 11.9436V12.0564C22.75 13.8942 22.75 15.3498 22.5969 16.489C22.4392 17.6614 22.1071 18.6104 21.3588 19.3588C20.6104 20.1071 19.6614 20.4392 18.489 20.5969C17.3498 20.75 15.8942 20.75 14.0564 20.75H9.94359C8.10583 20.75 6.65019 20.75 5.51098 20.5969C4.33856 20.4392 3.38961 20.1071 2.64124 19.3588C1.89288 18.6104 1.56076 17.6614 1.40314 16.489C1.24997 15.3498 1.24998 13.8942 1.25 12.0564V11.9436C1.24998 10.1058 1.24997 8.65019 1.40314 7.51098C1.56076 6.33856 1.89288 5.38961 2.64124 4.64124C3.38961 3.89288 4.33856 3.56076 5.51098 3.40314C6.65019 3.24997 8.10583 3.24998 9.94359 3.25L14.0564 3.25C15.8942 3.24998 17.3498 3.24997 18.489 3.40314ZM8.25 17C8.25 16.5858 8.58579 16.25 9 16.25H15C15.4142 16.25 15.75 16.5858 15.75 17C15.75 17.4142 15.4142 17.75 15 17.75H9C8.58579 17.75 8.25 17.4142 8.25 17Z" fill="#4D4D4D" />
                </svg>
            </span>
        ),
        label: 'Subscription',
        url: '/subscription'
    },
    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.96173 18.9106L9.42605 18.3216L8.96173 18.9106ZM12 5.50039L11.4596 6.02049C11.601 6.16738 11.7961 6.25039 12 6.25039C12.2039 6.25039 12.399 6.16738 12.5404 6.02049L12 5.50039ZM15.0383 18.9106L15.5026 19.4996L15.0383 18.9106ZM8.96173 18.9106L9.42605 18.3216C7.91039 17.1268 6.25307 15.96 4.93829 14.4795C3.64922 13.028 2.75 11.3343 2.75 9.13685H2H1.25C1.25 11.8024 2.3605 13.8358 3.81672 15.4756C5.24723 17.0864 7.07077 18.375 8.49742 19.4996L8.96173 18.9106ZM2 9.13685H2.75C2.75 6.98599 3.96537 5.18228 5.62436 4.42395C7.23607 3.68723 9.40166 3.88233 11.4596 6.02049L12 5.50039L12.5404 4.98029C10.0985 2.44328 7.26409 2.02514 5.00076 3.05972C2.78471 4.07268 1.25 6.42479 1.25 9.13685H2ZM8.96173 18.9106L8.49742 19.4996C9.00965 19.9034 9.55954 20.334 10.1168 20.6597C10.6739 20.9852 11.3096 21.2498 12 21.2498V20.4998V19.7498C11.6904 19.7498 11.3261 19.629 10.8736 19.3646C10.4213 19.1003 9.95208 18.7363 9.42605 18.3216L8.96173 18.9106ZM15.0383 18.9106L15.5026 19.4996C16.9292 18.375 18.7528 17.0864 20.1833 15.4756C21.6395 13.8358 22.75 11.8024 22.75 9.13685H22H21.25C21.25 11.3343 20.3508 13.028 19.0617 14.4795C17.7469 15.96 16.0896 17.1268 14.574 18.3216L15.0383 18.9106ZM22 9.13685H22.75C22.75 6.42479 21.2153 4.07268 18.9992 3.05972C16.7359 2.02514 13.9015 2.44328 11.4596 4.98029L12 5.50039L12.5404 6.02049C14.5983 3.88233 16.7639 3.68723 18.3756 4.42395C20.0346 5.18228 21.25 6.98599 21.25 9.13685H22ZM15.0383 18.9106L14.574 18.3216C14.0479 18.7363 13.5787 19.1003 13.1264 19.3646C12.6739 19.629 12.3096 19.7498 12 19.7498V20.4998V21.2498C12.6904 21.2498 13.3261 20.9852 13.8832 20.6597C14.4405 20.334 14.9903 19.9034 15.5026 19.4996L15.0383 18.9106Z" fill="#4D4D4D" />
                </svg>
            </span>
        ),
        label: 'Referrals',
        url: '/referral'
    },
    {
        icon: (
            <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 8H10" stroke="#4D4D4D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20.8333 9H18.2308C16.4465 9 15 10.3431 15 12C15 13.6569 16.4465 15 18.2308 15H20.8333C20.9167 15 20.9583 15 20.9935 14.9979C21.5328 14.965 21.9623 14.5662 21.9977 14.0654C22 14.0327 22 13.994 22 13.9167V10.0833C22 10.006 22 9.96726 21.9977 9.9346C21.9623 9.43384 21.5328 9.03496 20.9935 9.00214C20.9583 9 20.9167 9 20.8333 9Z" stroke="#4D4D4D" strokeWidth="1.5" />
                    <path d="M20.965 9C20.8873 7.1277 20.6366 5.97975 19.8284 5.17157C18.6569 4 16.7712 4 13 4L10 4C6.22876 4 4.34315 4 3.17157 5.17157C2 6.34315 2 8.22876 2 12C2 15.7712 2 17.6569 3.17157 18.8284C4.34315 20 6.22876 20 10 20H13C16.7712 20 18.6569 20 19.8284 18.8284C20.6366 18.0203 20.8873 16.8723 20.965 15" stroke="#4D4D4D" strokeWidth="1.5" />
                    <path d="M17.9912 12H18.0002" stroke="#4D4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        ),
        label: 'Payments',
        url: '/wallet'
    },
    {
        icon: (
            <span style={{fontSize: '22px', color: '#4D4D4D'}}>
                <i className='fab fa-whatsapp'></i>
            </span>
        ),
        label: 'Support',
        url: 'https://api.whatsapp.com/send?phone=919266391960',
        external: true
    }
]

const MainMenu: React.FC<MainMenuProps> = ({ visible, hide }) => {
    const mode = useSelector((state: RootState) => state.theme.mode);
    const influencer = useSelector((state: RootState) => state.influencer.influencer);
    const menuRef = useRef<HTMLDivElement>(null)

    const isTestUser = useSelector((state: RootState) => !!state.influencer.influencer?.is_test);

    const userMenuItems = [...menuItems.slice(0, menuItems.length - 1), ...(isTestUser ? testUserMenuItems : []), menuItems[menuItems.length - 1]];

    // close menu when clicked outside menu using useOutsideClick hook
    useOutsideClick(menuRef, hide)

    const appURL = useMemo(() => {
        if(!mobileOS) return '';
        return addAppDownloadUTMParams(MOBILE_APP_URLS[mobileOS]);
    }, [mobileOS]);

    return (
        <React.Fragment>
            <div id="menu-main" ref={menuRef} className={`menu menu-box-left rounded-0 ${visible ? 'menu-active' : ''}`} data-menu-width="280" data-menu-active="nav-welcome">
                <div className={`sidebar ${mode === 'dark' ? 'dark-mode' : ''}`}>
                    <Link onClick={() => hide()} to={{ pathname: '/i/me' }} className="menu-profile-card">
                        <div className="menu-profile-card-influencer">
                            {/* <img src={influencer?.profile_picture_url} className='menu-profile-card-influencer-image' alt='influencerprofilepic' /> */}
                            <span className='menu-profile-card-influencer-name'>{influencer?.name}</span>
                        </div>
                        <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M9 5L15 12L9 19" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </Link>
                    <div className="menu-items">
                        {
                            newMenuItems.map((item) => {
                                return (
                                    <Link target={item.external ? '_blank' : ''} to={item.url} onClick={() => hide()} className="new-menu-item" key={item.url}>
                                        <div className="d-flex align-items-center gap-2">
                                            {item.icon}
                                            <span className="menu-item-label">{item.label}</span>
                                        </div>
                                        <span>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 5L15 12L9 19" stroke="#007AFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                    </Link>
                                )
                            })
                        }
                        {
                            mobileOS &&
                            <a href={appURL} target='_blank' className='new-menu-item text-dark'>
                                <div className="d-flex align-items-center gap-2">
                                    <span><svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M8 3H16C18.2091 3 19 3.79086 19 6V18C19 20.2091 18.2091 21 16 21H8C5.79086 21 5 20.2091 5 18V6C5 3.79086 5.79086 3 8 3Z"
                                            stroke="#4D4D4D"
                                            strokeWidth="1.5"
                                            strokeLinejoin="round"
                                        />

                                        <path
                                            d="M10 6H14"
                                            stroke="#4D4D4D"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />

                                        <path
                                            d="M11.99 17H12"
                                            stroke="#4D4D4D"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    </span> Get the App</div> <span>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9 5L15 12L9 19" stroke="#007AFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span></a>
                        }
                    </div>

                </div>
                <div className="bottom-nav-items mt-3">
                    <Link to={'/logout'} onClick={() => hide()} className="new-menu-item" >
                        <div className="d-flex align-items-center gap-2">
                            <span>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.9453 1.25C13.5778 1.24998 12.4754 1.24996 11.6085 1.36652C10.7084 1.48754 9.95048 1.74643 9.34857 2.34835C8.82363 2.87328 8.55839 3.51836 8.41916 4.27635C8.28387 5.01291 8.25799 5.9143 8.25196 6.99583C8.24966 7.41003 8.58357 7.74768 8.99778 7.74999C9.41199 7.7523 9.74964 7.41838 9.75194 7.00418C9.75803 5.91068 9.78643 5.1356 9.89448 4.54735C9.99859 3.98054 10.1658 3.65246 10.4092 3.40901C10.686 3.13225 11.0746 2.9518 11.8083 2.85315C12.5637 2.75159 13.5648 2.75 15.0002 2.75H16.0002C17.4356 2.75 18.4367 2.75159 19.1921 2.85315C19.9259 2.9518 20.3144 3.13225 20.5912 3.40901C20.868 3.68577 21.0484 4.07435 21.1471 4.80812C21.2486 5.56347 21.2502 6.56459 21.2502 8V16C21.2502 17.4354 21.2486 18.4365 21.1471 19.1919C21.0484 19.9257 20.868 20.3142 20.5912 20.591C20.3144 20.8678 19.9259 21.0482 19.1921 21.1469C18.4367 21.2484 17.4356 21.25 16.0002 21.25H15.0002C13.5648 21.25 12.5637 21.2484 11.8083 21.1469C11.0746 21.0482 10.686 20.8678 10.4092 20.591C10.1658 20.3475 9.99859 20.0195 9.89448 19.4527C9.78643 18.8644 9.75803 18.0893 9.75194 16.9958C9.74964 16.5816 9.41199 16.2477 8.99778 16.25C8.58357 16.2523 8.24966 16.59 8.25196 17.0042C8.25799 18.0857 8.28387 18.9871 8.41916 19.7236C8.55839 20.4816 8.82363 21.1267 9.34857 21.6517C9.95048 22.2536 10.7084 22.5125 11.6085 22.6335C12.4754 22.75 13.5778 22.75 14.9453 22.75H16.0551C17.4227 22.75 18.525 22.75 19.392 22.6335C20.2921 22.5125 21.0499 22.2536 21.6519 21.6517C22.2538 21.0497 22.5127 20.2919 22.6337 19.3918C22.7503 18.5248 22.7502 17.4225 22.7502 16.0549V7.94513C22.7502 6.57754 22.7503 5.47522 22.6337 4.60825C22.5127 3.70814 22.2538 2.95027 21.6519 2.34835C21.0499 1.74643 20.2921 1.48754 19.392 1.36652C18.525 1.24996 17.4227 1.24998 16.0551 1.25H14.9453Z" fill="#4D4D4D" />
                                    <path d="M15 11.25C15.4142 11.25 15.75 11.5858 15.75 12C15.75 12.4142 15.4142 12.75 15 12.75H4.02744L5.98809 14.4306C6.30259 14.7001 6.33901 15.1736 6.06944 15.4881C5.79988 15.8026 5.3264 15.839 5.01191 15.5694L1.51191 12.5694C1.34567 12.427 1.25 12.2189 1.25 12C1.25 11.7811 1.34567 11.573 1.51191 11.4306L5.01191 8.43056C5.3264 8.16099 5.79988 8.19741 6.06944 8.51191C6.33901 8.8264 6.30259 9.29988 5.98809 9.56944L4.02744 11.25H15Z" fill="#4D4D4D" />
                                </svg>

                            </span>
                            <span className="menu-item-label">Logout</span>
                        </div>
                        <span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 5L15 12L9 19" stroke="#007AFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </Link>
                </div>
            </div>
            {
                visible &&
                <div className="menu-hider menu-active"></div>
            }
        </React.Fragment>

    );
};

export default MainMenu;